import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, Building, ExternalLink } from 'lucide-react';

const Contact = () => {
  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const springUp = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    show: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { type: "spring", stiffness: 100, damping: 20 }
    }
  };

  return (
    <>
      <Helmet>
        <title>İletişim | RandevUPP 2.0</title>
        <meta name="description" content="RandevUPP 2.0 ile iletişime geçin. Sistemimizi kurmak ve kendi markanızla randevu almaya başlamak için bizi hemen arayın." />
        <meta property="og:title" content="İletişim | RandevUPP 2.0" />
        <meta property="og:description" content="RandevUPP 2.0 ile iletişime geçin. Sistemimizi kurmak için bizi hemen arayın veya mail atın." />
      </Helmet>

      <motion.div 
        variants={staggerContainer}
        initial="hidden"
        animate="show"
        className="py-16 md:py-24"
      >
        <motion.div variants={springUp} className="text-center mb-16 px-4">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-6">
            Sizi Dinlemeye Hazırız
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            RandevUPP 2.0 hakkında detaylı bilgi almak, sistemi işletmenize kurmak veya destek talepleriniz için bize anında ulaşın. Form doldurmakla zaman kaybetmeyin, direkt iletişim kurun.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto px-4">
          {/* Direct Action Buttons */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12">
            <motion.a 
              variants={springUp}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="tel:+908508403917" 
              className="flex flex-col items-center justify-center gap-4 p-8 rounded-[2.5rem] bg-gradient-to-tr from-primary-600 to-primary-500 text-white shadow-xl hover:shadow-primary-500/40 transition-all group"
            >
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                <Phone size={32} />
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-primary-100 mb-1">Bizi Hemen Arayın</div>
                <div className="text-xl font-bold tracking-tight">+90 850 840 3917</div>
              </div>
            </motion.a>

            <motion.a 
              variants={springUp}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="https://wa.me/908508403917?text=Merhaba,%20RandevUPP%202.0%20hakkında%20bilgi%20almak%20istiyorum." 
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center justify-center gap-4 p-8 rounded-[2.5rem] bg-gradient-to-tr from-green-600 to-green-500 text-white shadow-xl hover:shadow-green-500/40 transition-all group"
            >
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.489-1.761-1.663-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                </svg>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-green-100 mb-1">WhatsApp Destek</div>
                <div className="text-xl font-bold tracking-tight">+90 850 840 3917</div>
              </div>
            </motion.a>

            <motion.a 
              variants={springUp}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="mailto:contact@vireliosoft.com" 
              className="flex flex-col items-center justify-center gap-4 p-8 rounded-[2.5rem] glass-panel group hover:border-gray-300 dark:hover:border-gray-600 transition-all sm:col-span-2 md:col-span-1"
            >
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300 group-hover:rotate-12 transition-transform duration-300 shadow-inner">
                <Mail size={32} />
              </div>
              <div className="text-center overflow-hidden">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">E-Posta Gönderin</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white truncate">contact@vireliosoft.com</div>
              </div>
            </motion.a>
          </div>

          {/* Corporate Info Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div variants={springUp} className="glass-panel p-8 rounded-[2.5rem] hover:-translate-y-2 transition-transform duration-300">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Building className="text-primary-500" /> Kurumsal Bilgiler
              </h3>
              <ul className="space-y-5 text-gray-600 dark:text-gray-300">
                <li className="flex items-start gap-3">
                  <div className="mt-1 font-semibold text-gray-900 dark:text-white w-24">Yüklenici Firma:</div>
                  <div className="flex-1">
                    RandevUPP bir <a href="https://www.vireliosoft.com" target="_blank" rel="noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1 font-medium">Vireliosoft <ExternalLink size={14}/></a> Yazılım Çözümleri ürünüdür.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1 font-semibold text-gray-900 dark:text-white w-24">V. Dairesi:</div>
                  <div className="flex-1 font-medium">Pamukkale Vergi Dairesi</div>
                </li>
              </ul>
            </motion.div>

            <motion.div variants={springUp} className="glass-panel p-8 rounded-[2.5rem] hover:-translate-y-2 transition-transform duration-300">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <MapPin className="text-primary-500" /> Merkez Ofis
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                Kınıklı Mah. 6033. Sok. No: 1 Vireliosoft Yazılım Çözümleri<br/>
                Pamukkale / Denizli
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Contact;
